/**
 * Forms Management helpers — timestamps, money, errors and key casing.
 *
 * Timestamps on the wire are yyyyMMddHHmmss numbers, already localized to the caller by
 * the backend, so they are read and written as local wall-clock time.
 */

import { FORMS_ENTRY_STATUS, FORMS_FORM_STATUS, FORMS_PAYMENT_STATUS } from '@actschurch/ccms-sdk';

export { FORMS_ENTRY_STATUS, FORMS_FORM_STATUS, FORMS_PAYMENT_STATUS };

export const FORMS_BASE_PATH = '/app/forms';

export const formPath = (id: number | string, tab?: string) =>
  `${FORMS_BASE_PATH}/forms/${id}${tab ? `/${tab}` : ''}`;

export const entryPath = (formId: number | string, entryId: number | string) =>
  `${FORMS_BASE_PATH}/forms/${formId}/entries/${entryId}`;

// ---------------------------------------------------------------- timestamps

const pad = (n: number) => String(n).padStart(2, '0');

/** Date → yyyyMMddHHmmss (local wall time). */
export function toStamp(date: Date | null | undefined): number | undefined {
  if (!date || Number.isNaN(date.getTime())) return undefined;
  return Number(
    `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}${pad(date.getHours())}${pad(
      date.getMinutes()
    )}${pad(date.getSeconds())}`
  );
}

/** Start / end of the given day as a stamp (inclusive range filters). */
export const toDayStartStamp = (d: Date | null | undefined) => {
  if (!d) return undefined;
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return toStamp(x);
};
export const toDayEndStamp = (d: Date | null | undefined) => {
  if (!d) return undefined;
  const x = new Date(d);
  x.setHours(23, 59, 59, 0);
  return toStamp(x);
};

/** yyyyMMddHHmmss (or yyyyMMdd) → Date. */
export function stampToDate(stamp: number | string | null | undefined): Date | null {
  if (stamp === null || stamp === undefined || stamp === '' || Number(stamp) <= 0) return null;
  const s = String(Math.trunc(Number(stamp))).padEnd(14, '0');
  const d = new Date(
    Number(s.slice(0, 4)),
    Number(s.slice(4, 6)) - 1,
    Number(s.slice(6, 8)),
    Number(s.slice(8, 10)),
    Number(s.slice(10, 12)),
    Number(s.slice(12, 14))
  );
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatStamp(stamp: number | string | null | undefined, fallback = '—'): string {
  const d = stampToDate(stamp);
  return d ? d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' }) : fallback;
}

export function formatStampDateTime(stamp: number | string | null | undefined, fallback = '—'): string {
  const d = stampToDate(stamp);
  return d
    ? d.toLocaleString(undefined, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : fallback;
}

/** Short day label for chart axes ("12 Sep"). `day` may be yyyyMMdd or a full stamp. */
export function formatDayLabel(day: number | string): string {
  const d = stampToDate(day);
  return d ? d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : String(day);
}

// ---------------------------------------------------------------- money

export function formatMoney(amount: number | null | undefined, currency = 'ZAR'): string {
  const value = Number(amount ?? 0);
  try {
    return new Intl.NumberFormat('en-ZA', { style: 'currency', currency: currency || 'ZAR' }).format(value);
  } catch {
    return `R ${value.toFixed(2)}`;
  }
}

// ---------------------------------------------------------------- errors

/** Message from an SDK CCMSError / Error / anything. */
export function errorMessage(err: unknown, fallback = 'Something went wrong'): string {
  if (!err) return fallback;
  const e = err as { message?: string; errors?: unknown; raw?: { message?: string } };
  const errors = Array.isArray(e.errors) ? (e.errors as unknown[]).filter((x) => typeof x === 'string') : [];
  const base = e.message || e.raw?.message || fallback;
  return errors.length ? `${base} — ${errors.join('; ')}` : base;
}

/** HTTP status of an SDK error (CCMSError.status), if any. */
export function errorStatus(err: unknown): number | undefined {
  const e = err as { status?: number; statusCode?: number } | null;
  return e?.status ?? e?.statusCode ?? undefined;
}

/** Field-level errors ("fieldKey: message") from a 400 response. */
export function fieldErrors(err: unknown): Record<string, string> {
  const e = err as { errors?: unknown };
  const out: Record<string, string> = {};
  if (Array.isArray(e?.errors)) {
    for (const item of e.errors as unknown[]) {
      if (typeof item !== 'string') continue;
      const idx = item.indexOf(':');
      if (idx > 0) out[item.slice(0, idx).trim()] = item.slice(idx + 1).trim();
    }
  }
  return out;
}

// ---------------------------------------------------------------- key casing

/**
 * The SDK's response normaliser lower-cases the first letter of every JSON key —
 * including dictionary keys (answers, export rows, member mapped values). Look a key
 * up under both spellings.
 */
export function lowerFirst(key: string): string {
  return key ? key.charAt(0).toLowerCase() + key.slice(1) : key;
}
export function upperFirst(key: string): string {
  return key ? key.charAt(0).toUpperCase() + key.slice(1) : key;
}
export function readKey<T = unknown>(obj: Record<string, unknown> | null | undefined, key: string): T | undefined {
  if (!obj) return undefined;
  if (key in obj) return obj[key] as T;
  const lf = lowerFirst(key);
  if (lf in obj) return obj[lf] as T;
  return undefined;
}

// ---------------------------------------------------------------- answers

/** Human-readable rendering of an answer value. */
export function formatAnswer(value: unknown, options?: { value: string; label: string }[] | null): string {
  if (value === null || value === undefined || value === '') return '—';
  const labelOf = (v: unknown) => options?.find((o) => o.value === String(v))?.label ?? String(v);
  if (Array.isArray(value)) {
    if (value.length === 0) return '—';
    if (value.every((v) => typeof v !== 'object' || v === null)) return value.map(labelOf).join(', ');
    return `${value.length} item${value.length === 1 ? '' : 's'}`;
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') {
    return Object.values(value as Record<string, unknown>)
      .filter((v) => v !== null && v !== undefined && v !== '')
      .map(String)
      .join(', ') || '—';
  }
  return labelOf(value);
}

export const ACCESS_LEVEL_LABELS: Record<number, string> = { 1: 'Viewer', 2: 'Editor', 3: 'Owner' };

/** Trigger a browser download of text content. */
export function downloadText(content: string, filename: string, mime = 'text/plain') {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
